import { ChangeAnalysisScopeModalContent } from '@modules/analysis/components/ChangeAnalysisScopeModalContent';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useIsSearchToFeedEnabled } from '@modules/common/hooks';
import { ArtefactType } from '@modules/common/types';
import { useUpdateSearchDashboard } from '@modules/feed/hooks';
import { SearchActionType } from '@modules/feed/store/searchActionType';
import { useSearchStore } from '@modules/feed/store/searchStore';
import { getSearchLink } from '@modules/feed/utils/getSearchLink';
import { useCallback } from 'react';
import { createAnalyticsTracker } from 'v2/analytics/events';
import { Scope } from 'v2/core/entities/scope';
import { mapToScope } from 'v2/core/repository/edge-server/analysis/toEdgeAnalysisMappers';
import useFullscreenLoader from 'v2/core/services/loader/useFullscreenLoader';
import { useGetUser } from 'v2/core/services/user';

export const trackSearchDashboardEvents =
  createAnalyticsTracker('searchDashboard');

export const useChangeSearchScope = () => {
  const {
    id: userID,
    tenant: { id: tenantID, slug: tenantSlug },
  } = useGetUser();

  const { openModal, closeModal } = useMuiModalContext();
  const { showErrorNotification } = useNotificationContext();

  const searchDashboard = useSearchStore(({ data }) => data);
  const dispatch = useSearchStore(({ dispatch }) => dispatch);

  const isSearchToFeedEnabled = useIsSearchToFeedEnabled();

  const { updateSearchDashboard, isLoading } = useUpdateSearchDashboard({
    searchDashboard: searchDashboard!,
    onError: () => {
      showErrorNotification({
        contentComponent: `Failed to update scope of ${
          isSearchToFeedEnabled ? 'Feed' : 'Saved Search'
        }.`,
        props: { isClosable: true },
      });
    },
  });

  useFullscreenLoader({ isLoading });

  const updateSavedSearchScope = useCallback(
    async (scope: Scope) => {
      try {
        if (!searchDashboard?.editToken) {
          return;
        }

        await updateSearchDashboard({
          variables: {
            ...searchDashboard,
            userID,
            tenantID,
            scope: mapToScope(scope),
            editToken: searchDashboard.editToken ?? '',
            createdBy: userID,
          },
        });
        dispatch({
          type: SearchActionType.SET_DATA,
          payload: {
            scope,
          },
        });
        const artefactLink = getSearchLink({
          name: searchDashboard.title,
          token: searchDashboard.viewToken,
          tenantSlug,
          isSearchToFeedEnabled,
        });
        trackSearchDashboardEvents({
          eventName: 'Search Dashboard Scope Changed',
          artefactLink,
          name: searchDashboard.title,
          scope,
        });
        closeModal();
      } catch {
        // do nothing, already handled by useUpdateSearchDashboard
      }
    },
    [
      closeModal,
      dispatch,
      searchDashboard,
      tenantID,
      tenantSlug,
      updateSearchDashboard,
      userID,
      isSearchToFeedEnabled,
    ],
  );

  const openChangeAnalysisScopeModal = useCallback(
    () =>
      openModal({
        contentComponent: () => (
          <ChangeAnalysisScopeModalContent
            artefactType={ArtefactType.Search}
            analysisScope={searchDashboard?.scope ?? Scope.PRIVATE}
            onScopeSave={updateSavedSearchScope}
            onClose={closeModal}
          />
        ),
        modalProps: {
          fullWidth: true,
          maxWidth: 'sm',
          PaperProps: {
            elevation: 3,
          },
        },
      }),
    [closeModal, openModal, searchDashboard?.scope, updateSavedSearchScope],
  );

  return openChangeAnalysisScopeModal;
};
